<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch} from "vue";
import {RouterLink, useRouter} from "vue-router";
import {useIntersectionObserver, useStorage, watchDebounced} from "@vueuse/core";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import {deleteGroup, getGroups as getGroupsFromApi} from "@/Services/groups";
import {removeGroupMember} from "@/Services/group-members";
import {Button, Dialog, Drawer, InputText, Menu, Message} from "@/Components/Library";
import {AlphabetList, FilterBar, PageLoading, PageTitle} from "@/Components/Global";
import GroupItem from "./GroupItem.vue";
import {getInvitations} from "@/Services/invitations";
import User from "@/Models/User";
import {Icon} from "@iconify/vue";
import useEcho from "@/Composables/echo";
import PageContainer from "@/Components/Global/PageContainer.vue";
import GroupsIndexEvents from "./groups-index-events";
import GroupsIndexFilters from "./GroupsIndexFilters.vue";
import Str from "@/Utils/Str.ts";

const $user = new User();
const confirm = useConfirm();
const toast = useToast();
const router = useRouter();
const state = reactive({
    error: false,
    show: {
        filters: false,
        groupMenu: false,
        invitation: false,
    },
    loading: {
        groups: false,
    },
});
const params = reactive({
    page: 0,
    filter: {
        search: null,
        role: null,
    },
});
const filters = ref({
    role: null,
});
const showInvitations = useStorage("show.invitations.groups", true);
const showGroupMenu = computed({get: () => !!state.show.groupMenu, set: (val) => (state.show.groupMenu = val)});

const groups = ref([]);
const invitations = ref([]);
getInvitations({for: "group", status: "pending"}).then(({data}) => {
    console.log(data);
    invitations.value = data;
});

const groupsByLetter = computed(() => {
    const groupsByLetter = new Map<string, any[]>();
    groups.value?.forEach((group) => {
        const letter = group.name[0].toUpperCase();
        if (!groupsByLetter.has(letter)) {
            groupsByLetter.set(letter, []);
        }
        groupsByLetter.get(letter).push(group);
    });
    return groupsByLetter;
});

const search = ref<string | null>();

const totalActiveFilters = computed(() => {
    return Object.values(filters.value).filter((f) => f).length;
});

const getGroups = async () => {
    try {
        state.loading.groups = true;
        const {data, total} = await getGroupsFromApi({
            ...params,
            include: ["icons", "membersCount", "group_questionsCount"],
        });

        if (params.page > 0) {
            groups.value = [...groups.value, ...data];
        } else {
            groups.value = data;
        }

        loadMore.value = data.length && groups.value.length < total;

        if (!total && !search.value && !totalActiveFilters.value) {
            showInvitations.value = true;
        }
    } catch (e) {
        console.error(e);
        state.error = e.response?.data?.message || e.message || "An error occurred";
    } finally {
        state.loading.groups = false;
    }
};
getGroups();

// Menu
const menu = ref<Menu>();
const selectedGroup = computed(() => groups.value.find((group) => group.id === state.show.groupMenu));
const groupMenuItemsBase = {
    editGroupMenuItem: {
        show: ["is_owner", true],
        label: "Edit",
        icon: "prime:pencil",
        command: (event) => {
            router.push({
                name: "groups.edit",
                params: {group: selectedGroup.value?.id},
            });
            state.show.groupMenu = false;
        },
    },
    deleteGroupMenuItem: {
        show: ["is_owner", true],
        label: "Archive",
        icon: "prime:trash",
        command: async (event) => {
            const currentGroup = selectedGroup.value;
            confirm.require({
                header: `Archive "${currentGroup.name}"`,
                message: "Are you sure you want to archive this group?",
                acceptLabel: "Yes",
                rejectLabel: "No",
                acceptClass:
                    "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
                rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
                accept: async () => {
                    await deleteGroup(currentGroup.id);
                    toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: "You have archived the group.",
                        life: 3000,
                    });
                    groups.value = groups.value.filter((group) => group.id !== currentGroup.id);
                },
            });
            state.show.groupMenu = false;
        },
        class: "text-red-600 hover:bg-red-100",
    },
    leaveGroupMenuItem: {
        show: ["is_owner", false],
        label: "Leave",
        icon: "prime:sign-out",
        command: async () => {
            const currentGroup = selectedGroup.value;
            confirm.require({
                header: `Leave "${currentGroup.name}"`,
                message: "Are you sure you want to leave this group?",
                acceptLabel: "Yes",
                rejectLabel: "No",
                acceptClass:
                    "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
                rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
                accept: async () => {
                    await removeGroupMember(currentGroup.id, $user.id);
                    toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: "You have left the group.",
                        life: 3000,
                    });
                    groups.value = groups.value.filter((group) => group.id !== currentGroup.id);
                },
            });
            state.show.groupMenu = false;
        },
        class: "text-red-600 hover:bg-red-100",
    },
};
const groupMenuItems = ref([
    {
        label: "Group",
        items: [],
    },
]);

const toggleGroupMenu = (group) => {
    groupMenuItems.value[0].items = Object.values(groupMenuItemsBase).filter((item) => {
        if (item.show) {
            return group[item.show[0]] === item.show[1];
        }
        return true;
    });
    groupMenuItems.value[0].label = Str.of(group.name ?? "Group").truncate().value;

    state.show.groupMenu = group.id;
};

const clearFilters = () => {
    filters.value.role = null;
    applyFilters();
};

const loadMore = ref(false);
const loadMoreIntersection = useTemplateRef("loadMoreIntersection");
const {stop} = useIntersectionObserver(
    loadMoreIntersection,
    ([{isIntersecting}], observerElement) => isIntersecting && loadMore.value && params.page++,
);

const applyFilters = () => {
    const newParams = {
        page: 0,
        filter: {
            ...params.filter,
            role: filters.value.role,
        },
    };
    Object.assign(params, newParams);
    state.show.filters = false;
};

const hasFilters = computed(() => {
    return Object.values(filters.value).some((f) => f);
});

useEcho(GroupsIndexEvents(groups, $user));

watchDebounced(
    search,
    async () => {
        loadMore.value = false;
        const newParams = {
            page: 0,
            filter: {
                ...params.filter,
                search: search.value,
            },
        };
        Object.assign(params, newParams);
    },
    {debounce: 500},
);

watch(() => params, getGroups, {deep: true});
</script>
<template>
    <PageContainer class="">
        <div class="mb-2">
            <PageTitle title="Groups" :show-back-button="false" />
        </div>
        <div v-if="invitations?.length" class="mb-3" v-auto-animate>
            <div v-if="showInvitations" class="flex flex-col gap-2">
                <template v-for="invitation in invitations" :key="invitation.id">
                    <Message v-if="invitation.for.id">
                        <template #container>
                            <div class="w-full flex-wrap items-center justify-between px-6 py-3 text-sm md:flex">
                                <div class="mb-2 text-center md:mb-0">
                                    <span
                                        class="cursor-pointer font-bold"
                                        @click="state.show.invitation = invitation.id"
                                    >
                                        {{ invitation.user?.name }}
                                    </span>
                                    invites you to connect and join
                                    <strong>{{ invitation.for?.name }}</strong>
                                </div>
                                <div class="flex items-center justify-center gap-3">
                                    <!--                                <Button-->
                                    <!--                                    text-->
                                    <!--                                    class="text-sm !font-normal text-red-600 hover:brightness-110"-->
                                    <!--                                    @click="confirmRespondToInvitation($event, invitation.id, 'decline')"-->
                                    <!--                                >-->
                                    <!--                                    Decline-->
                                    <!--                                </Button>-->
                                    <!--                                <Button-->
                                    <!--                                    class="border border-askr-green !px-3 !py-1 text-sm text-askr-green hover:brightness-110"-->
                                    <!--                                    @click="confirmRespondToInvitation($event, invitation.id, 'accept')"-->
                                    <!--                                >-->
                                    <!--                                    Accept-->
                                    <!--                                </Button>-->
                                    <RouterLink
                                        :to="{
                                            name: 'groups.invitations.show',
                                            params: {group: invitation.for.id, invitation: invitation.id},
                                        }"
                                    >
                                        <Button
                                            class="border border-askr-green !px-3 !py-1 text-sm text-askr-green hover:brightness-110"
                                        >
                                            View
                                        </Button>
                                    </RouterLink>
                                </div>
                            </div>
                        </template>
                    </Message>
                </template>
            </div>
            <div v-else>
                <Message @click="showInvitations = !showInvitations" class="cursor-pointer">
                    <template #container>
                        <div class="flex w-full items-center gap-2 px-4 py-3 text-sm">
                            <span
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-askr-green font-bold text-white"
                            >
                                {{ invitations?.length }}
                            </span>
                            <h3 class="font-bold">Invitations to groups</h3>
                        </div>
                    </template>
                </Message>
            </div>
            <div class="m-container mt-1 flex w-full justify-end">
                <button
                    type="button"
                    class="flex items-center gap-1 text-xs"
                    @click="showInvitations = !showInvitations"
                >
                    <Icon :icon="showInvitations ? 'akar-icons:chevron-up' : 'akar-icons:chevron-down'" />
                    {{ showInvitations ? "Hide" : "Show" }} invitations
                </button>
            </div>
        </div>
        <div class="-sticky top-2 z-10 mb-3">
            <InputText type="search" v-model="search" placeholder="Search groups" class="w-full" />
        </div>
        <div class="flex gap-6">
            <!-- filters -->
            <div class="hidden h-full w-full max-w-sm flex-none grow 2xl:block">
                <div class="sticky top-2">
                    <GroupsIndexFilters
                        v-model="filters"
                        :total-active-filters="totalActiveFilters"
                        @apply="applyFilters"
                        @clear="clearFilters"
                    />
                </div>
            </div>
            <!-- content -->
            <div class="m--container divide--y grow">
                <template v-for="[letter, groups] in groupsByLetter" :key="letter">
                    <AlphabetList :letter="letter" :vertical="true">
                        <template v-for="group in groups" :key="group.id">
                            <RouterLink
                                :to="{
                                    name: 'feed',
                                    params: {group: group.id},
                                }"
                            >
                                <GroupItem :group="group" @toggleGroupMenu="toggleGroupMenu" />
                            </RouterLink>
                        </template>
                    </AlphabetList>
                </template>
                <PageLoading v-if="state.loading.groups" class="pt-16" />
                <p v-if="state.error" class="mt-8 rounded border border-red-600 bg-red-50 p-2 text-center">
                    {{ state.error }}
                </p>
                <div
                    v-if="!groups?.length && !state.loading.groups"
                    class="flex grow flex-col items-center justify-center gap-2 p-6 sm:h-auto"
                >
                    <p class="mb-3 text-sm">
                        {{
                            search || totalActiveFilters
                                ? "No matching groups found."
                                : "You have not created any groups yet."
                        }}
                    </p>
                    <RouterLink :to="{name: 'groups.create'}">
                        <Button
                            class="bg-askr-green text-white hover:brightness-110"
                            :icon="{
                                icon: 'mdi:plus',
                                width: '24',
                            }"
                        >
                            Create a new group
                        </Button>
                    </RouterLink>
                </div>
            </div>
        </div>
        <div v-if="!state.loading.groups && !state.error && loadMore">
            <span ref="loadMoreIntersection"></span>
            <div class="flex justify-center">
                <Button
                    @click="params.page++"
                    :loading="!!state.loading.groups"
                    :disabled="!!state.loading.groups || !loadMore"
                >
                    {{ loadMore || !groups.length ? "Load more" : "No more results" }}
                </Button>
            </div>
        </div>
        <FilterBar v-model="state.show.filters" :total-active-filters="totalActiveFilters">
            <RouterLink :to="{name: 'groups.create'}">
                <Button
                    :icon="{icon: 'mdi:plus', width: '36'}"
                    rounded
                    class="pointer-events-auto h-14 w-14 bg-askr-green text-white shadow-stone-600"
                />
            </RouterLink>
        </FilterBar>
        <Dialog
            v-model:visible="showGroupMenu"
            modal
            dismissable-mask
            class="max-w-md overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <Menu ref="menu" :model="groupMenuItems" />
            </template>
        </Dialog>
        <Drawer
            v-model:visible="state.show.filters"
            position="bottom"
            header="Filters"
            class="max-h-dvh max-w-3xl overflow-y-auto"
        >
            <div class="pb-12">
                <GroupsIndexFilters
                    v-model="filters"
                    :total-active-filters="totalActiveFilters"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
        </Drawer>
    </PageContainer>
</template>
<style scoped></style>
